import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { 
  Home, FileText, Briefcase, Users, PoundSterling, Image, 
  Phone, LogIn, UserPlus, HelpCircle
} from 'lucide-react';

const sitemapSections = [
  {
    title: 'Main Pages',
    links: [
      { name: 'Home', path: '/', icon: Home, description: 'Welcome to Expert-Handyman' },
      { name: 'Get a Quote', path: '/quote', icon: FileText, description: 'Tell us about your job and get a free quote' },
      { name: 'Our Services', path: '/services', icon: Briefcase, description: 'View all handyman services we offer' },
      { name: 'Price Comparison', path: '/prices', icon: PoundSterling, description: 'Compare our prices vs typical UK rates' },
      { name: 'Our Work', path: '/gallery', icon: Image, description: 'Before & after gallery of completed projects' },
      { name: 'About Us', path: '/about', icon: Users, description: 'Learn about Expert-Handyman' },
    ]
  },
  {
    title: 'Customer Account',
    links: [
      { name: 'Login', path: '/login', icon: LogIn, description: 'Sign in to your account' },
      { name: 'Register', path: '/register', icon: UserPlus, description: 'Create a new customer account' },
      { name: 'My Dashboard', path: '/dashboard', icon: Home, description: 'View your quotes and bookings' },
    ]
  },
  {
    title: 'Services We Offer',
    links: [
      { name: 'Decorating', path: '/quote', description: 'Interior painting, wallpapering & finishing' },
      { name: 'Carpentry', path: '/quote', description: 'Door fitting, shelving, storage & joinery' },
      { name: 'Outdoors & Garden', path: '/quote', description: 'Fencing, paving, decking & landscaping' },
      { name: 'Electrics', path: '/quote', description: 'Lighting, sockets & alarm installation' },
      { name: 'Plastering & Tiling', path: '/quote', description: 'Walls, ceilings, floors & bathrooms' },
      { name: 'General Tasks', path: '/quote', description: 'TV mounting, blinds, locks & odd jobs' },
      { name: 'Half Day Rate', path: '/quote', description: '4 hours of work for £100' },
      { name: 'Full Day Rate', path: '/quote', description: '8 hours of work for £180' },
    ]
  },
  {
    title: 'Areas We Cover',
    areas: [
      'Stockport', 'Manchester', 'Altrincham', 'Sale', 'Cheadle',
      'Wilmslow', 'Macclesfield', 'Didsbury', 'Chorlton', 'Stretford',
      'Bramhall', 'Poynton', 'Hazel Grove', 'Marple', 'Gatley',
      'Heald Green', 'Handforth', 'Knutsford', 'Hale', 'Timperley'
    ]
  }
];

export const Sitemap = () => {
  return (
    <div className="min-h-screen bg-muted/30 py-12">
      <SEO 
        title="Sitemap - Expert-Handyman"
        description="Complete sitemap of Expert-Handyman website. Find all our pages, services, and areas we cover in Manchester, Stockport, and Cheshire."
        keywords="sitemap, expert handyman pages, handyman services Manchester, handyman Stockport, handyman Cheshire"
      />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] mb-4">Sitemap</h1>
          <p className="text-muted-foreground">
            Find your way around Expert-Handyman. All our pages and services in one place.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {sitemapSections.map((section) => (
            <div key={section.title} className="bg-white rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-bold font-['Outfit'] mb-4 pb-2 border-b">
                {section.title}
              </h2>
              
              {section.links && (
                <ul className="space-y-3">
                  {section.links.map((link) => (
                    <li key={link.path}>
                      <Link 
                        to={link.path}
                        className="flex items-start gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors group"
                      >
                        {link.icon && (
                          <link.icon className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <span className="font-medium group-hover:text-primary transition-colors">
                            {link.name}
                          </span>
                          {link.description && (
                            <p className="text-sm text-muted-foreground">{link.description}</p>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              
              {section.areas && (
                <div className="flex flex-wrap gap-2">
                  {section.areas.map((area) => (
                    <span 
                      key={area}
                      className="px-3 py-1 bg-muted rounded-full text-sm"
                    >
                      {area}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Additional SEO Content */}
        <div className="mt-12 bg-white rounded-xl p-6 shadow-sm">
          <h2 className="text-xl font-bold font-['Outfit'] mb-4">About Expert-Handyman</h2>
          <div className="prose prose-sm max-w-none text-muted-foreground">
            <p>
              Expert-Handyman provides professional handyman services across Greater Manchester and Cheshire. 
              We offer a wide range of property maintenance services including decorating, carpentry, 
              plumbing basics, garden work, and general repairs.
            </p>
            <p className="mt-3">
              Our services are available in Stockport, Manchester city centre, Altrincham, Sale, Cheadle, 
              Wilmslow, Macclesfield, Didsbury, Chorlton, Stretford, and surrounding areas. We pride ourselves 
              on transparent pricing, quality workmanship, and exceptional customer service.
            </p>
            <p className="mt-3">
              All work is fully insured with £15 million public liability coverage and comes with a 
              12-month workmanship guarantee. We do not charge VAT, meaning you save 20% compared to 
              VAT-registered tradespeople.
            </p>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            Need help? Contact us via WhatsApp or get a free quote online.
          </p>
          <p className="mt-1">
            © {new Date().getFullYear()} Expert-Handyman. Serving Manchester & Cheshire.
          </p>
        </div>
      </div>
    </div>
  );
};
